import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { Analytics, AnalyticsBucket, AnalyticsKpis } from "../../api";
import {
  CHART_AXIS_PROPS,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
  chartColor,
} from "../../lib/chart-colors";
import { fmtBucketLabel, fmtInt } from "../../lib/format";
import { InfoTooltip } from "../info-tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Skeleton } from "../ui/skeleton";

/**
 * Yangi mijozlar dinamikasi — KPI so'rovining bucket qatorlaridan (alohida
 * so'rov kerak emas, guruhlash KPI jadvalidagi `groupBy` bilan bir xil).
 *
 * Ustaxona-global: backend `new_clients` uchun filial filtrini ATAYIN
 * qo'llamaydi — karta izohi shuni aytadi.
 */
export function NewClientsTrendCard({
  kpis,
  clients,
  loading,
}: {
  kpis: AnalyticsKpis | null;
  clients: Analytics["clients"];
  loading: boolean;
}) {
  const { t } = useTranslation("analytics");
  const trend = useMemo(
    () =>
      (kpis?.buckets || []).map((b) => ({
        date: b.date,
        value: Number(b.metrics.new_clients),
      })),
    [kpis],
  );
  const groupBy: AnalyticsBucket | undefined = kpis?.group_by;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">
            {t("clientsTrend.title")} <InfoTooltip text={t("clientsTrend.hint")} />
          </CardTitle>
          <p className="text-xs text-muted-foreground tabular-nums">
            {t("newReturning.new")}: <strong>{fmtInt(clients.new_count)}</strong>
            {" · "}
            {t("newReturning.returning")}:{" "}
            <strong>{fmtInt(clients.returning_count)}</strong>
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {!kpis || !groupBy ? (
          loading ? (
            <Skeleton className="h-[220px] sm:h-[260px]" />
          ) : (
            <EmptyState title={t("empty.noInfo")} className="py-8" />
          )
        ) : (
          <div className="w-full h-[220px] sm:h-[260px]">
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid {...CHART_GRID_PROPS} vertical={false} />
                <XAxis
                  dataKey="date"
                  {...CHART_AXIS_PROPS}
                  tickFormatter={(v: string) => fmtBucketLabel(v, groupBy)}
                  interval="preserveStartEnd"
                  minTickGap={24}
                  tickMargin={8}
                />
                <YAxis {...CHART_AXIS_PROPS} allowDecimals={false} width={32} />
                <Tooltip
                  {...CHART_TOOLTIP_PROPS}
                  formatter={(v: number) => fmtInt(v)}
                  labelFormatter={(label: string) => fmtBucketLabel(label, groupBy)}
                />
                <Bar
                  dataKey="value"
                  name={t("newReturning.new")}
                  fill={chartColor("chart-2")}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
